import { Box, Typography, IconButton } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { BaseAccordion } from '../../../../../Accordion/BaseAccordion';
import { ValidationBadge } from '../../ValidationBadge/ValidationBadge';
import { ColumnMapping } from '../InstrumentIdentifiers/components/ColumnMapping/ColumnMapping';
import { LabelAutocompleteInput } from '../../../../../Infra/LabelAutocompleteInput/LabelAutocompleteInput';
import { CustomButton } from '../../../../../Infra/CustomButton/CustomButton';
import { CustomAutocomplete } from '../../../../../Infra/CustomAutocomplete/CustomAutocomplete';
import { getCurrencyDisplay } from './currencyData';
import { useCurrencySection } from './useCurrencySection';
import { CurrencySectionProps } from './types';
import './CurrencySection.scss';

export const CurrencySection = (props: CurrencySectionProps) => {
  const {
    title,
    templateId,
    instrumentType,
    sectionId,
    availableHeaders = [],
    expanded,
    onChange,
  } = props;

  const {
    currencyMappings,
    selectedCurrency,
    setSelectedCurrency,
    availableCurrencies,
    addCurrency,
    removeCurrency,
    formatVariationsAsLabels,
    removeVariation,
    addVariation,
    selectedColumn,
    currencySuggestions,
    validationResult,
  } = useCurrencySection(props);

  return (
    <BaseAccordion
      title={title}
      expanded={expanded}
      onChange={onChange}
      badge={<ValidationBadge validationResult={validationResult} />}
      className="currencySection"
    >
      <Box>
        {/* Column Mapping */}
        <ColumnMapping
          templateId={templateId}
          instrumentType={instrumentType}
          sectionId={sectionId}
          availableHeaders={availableHeaders}
          singleMode={true}
        />

        {/* Add Currency Section */}
        <Typography variant="caption" className="addCurrencyTitle">
          ADD CURRENCY
        </Typography>
        <Box className="addCurrencySection">
          <CustomAutocomplete
            value={selectedCurrency}
            onChange={(_, value) => setSelectedCurrency(value)}
            options={availableCurrencies}
            getOptionLabel={option => getCurrencyDisplay(option.code)}
            placeholder="Select currency..."
            sx={{ flex: 1, maxWidth: 300 }}
          />
          <CustomButton
            variant="contained"
            onClick={() => {
              if (selectedCurrency) {
                addCurrency(selectedCurrency.code);
                setSelectedCurrency(null);
              }
            }}
            disabled={!selectedCurrency}
            size="small"
          >
            Add
          </CustomButton>
        </Box>

        {/* Currency Items Container */}
        <Box className="currencyContainer">
          {currencyMappings.map(mapping => (
            <Box key={mapping.currency} className="currencyItem">
              {/* Header: Label + Delete */}
              <Box className="currencyHeader">
                <Typography variant="caption" className="currencyLabel">
                  {getCurrencyDisplay(mapping.currency)}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => removeCurrency(mapping.currency)}
                  sx={{ color: 'error.main' }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* LabelAutocompleteInput */}
              <LabelAutocompleteInput
                showCheckboxes
                selectedColumn={selectedColumn}
                labels={formatVariationsAsLabels(mapping.currency)}
                onAdd={value => addVariation(mapping.currency, value)}
                onRemove={id => removeVariation(mapping.currency, id)}
                placeholder="Select or type currency variations..."
                disabled={!selectedColumn}
                customSuggestions={currencySuggestions}
              />
            </Box>
          ))}
        </Box>
      </Box>
    </BaseAccordion>
  );
};
